import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { FileEdit, Loader2, Maximize2, PenSquare, Save, Send } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { AsciiDocEditor } from '@/components/AsciiDocEditor';
import { DjotEditor } from '@/components/DjotEditor';
import { EditorFormatSelect } from '@/components/EditorFormatSelect';
import { EditorViewToggle, type EditorViewMode } from '@/components/EditorViewToggle';
import { FocusModeView } from '@/components/FocusModeView';
import { SaveStatusBadge, type SaveState } from '@/components/SaveStatusBadge';
import { MediaPickerDialog } from '@/components/media/MediaPickerDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/useToast';
import { useArticleDrafts } from '@/hooks/useArticleDrafts';
import { useDraftSync } from '@/hooks/useDraftSync';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useResolvePendingMedia } from '@/hooks/useMediaLibrary';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useWikiArticle, useWikiVersions } from '@/hooks/useWikiArticles';
import { normalizeWikiTitle, prettifySlug } from '@/lib/wikiSlug';
import { WIKI_ARTICLE_KIND } from '@/lib/nip54';
import { detectContentFormat, type ContentFormat } from '@/lib/contentFormat';
import { editorPlaceholder } from '@/lib/editorFormat';
import { computeEditorStats } from '@/lib/editorStats';
import { findPendingMediaHashes, resolvePendingMedia } from '@/lib/mediaMarkup';
import { expandWikiLinks } from '@/lib/wikiLinkSyntax';
import { insertBlockAtCursor } from '@/lib/djotEditing';

/** A point-in-time snapshot of the editor fields, for detecting unsaved changes. */
interface EditorSnapshot {
  title: string;
  summary: string;
  content: string;
}

export function ArticleEditPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: publishEvent, isPending } = useNostrPublish();
  const resolveMedia = useResolvePendingMedia();
  const [mediaOpen, setMediaOpen] = useState(false);
  const [viewMode, setViewMode] = useState<EditorViewMode | undefined>();
  /** Fullscreen (focus) mode: the only thing on screen is the editor. */
  const [focusMode, setFocusMode] = useState(false);
  /** Tracks an in-progress draft save so the status badge can show it distinctly from plain "dirty". */
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const drafts = useArticleDrafts();
  const draftSync = useDraftSync();

  const slug = rawSlug ? normalizeWikiTitle(decodeURIComponent(rawSlug)) : '';

  // A `?draft=` param means "resume this specific local draft" rather
  // than "prefill from what's on the relays". The draft wins entirely:
  // mixing a stale draft with a newer published version would lose one
  // of them silently, so resume is all-or-nothing.
  const draftId = searchParams.get('draft') ?? undefined;
  const draft = drafts.get(draftId);

  const { data: versions } = useWikiVersions(slug);
  const { data: ownVersion } = useWikiArticle(slug, user?.pubkey);
  const existingOtherVersion = versions?.find((v) => v.pubkey !== user?.pubkey);

  // Seeded synchronously from the draft when resuming. The draft store
  // reads localStorage synchronously (see `useArticleDrafts`), so a
  // resumed draft is already available on the very first render — doing
  // this here rather than in an effect avoids a frame of empty fields.
  const [title, setTitle] = useState(() => draft?.title ?? '');
  const [summary, setSummary] = useState(() => draft?.summary ?? '');
  const [content, setContent] = useState(() => draft?.content ?? '');

  // A blank new article defaults to Djot — it's the NIP-54-mandated
  // format, and asking the user for format before they've written
  // anything is friction for no benefit. Any format the user knowingly
  // switches to (or a draft/prefill detects) still wins.
  const [editFormatState, setEditFormat] = useState<ContentFormat | undefined>(
    draft?.content ? detectContentFormat(draft.content) : 'djot',
  );
  const [savedSnapshot, setSavedSnapshot] = useState<EditorSnapshot | null>(
    draft ? { title: draft.title, summary: draft.summary, content: draft.content } : null,
  );

  /**
   * Which source the editor fields were last initialized from: a draft
   * id, or `''` for relay prefill, or `null` for "not yet initialized".
   *
   * This deliberately isn't a boolean "done" flag. Comparing against the
   * *current* `draftId` means arriving at a draft while already sitting
   * in the editor — or switching from one draft to another — correctly
   * re-initializes, instead of leaving the previous draft's text on
   * screen because a flag was already set.
   */
  const [loadedFrom, setLoadedFrom] = useState<string | null>(draft ? draft.id : null);

  useSeoMeta({
    title: `Edit ${prettifySlug(slug)} — Wikistar`,
  });

  // Resume a draft on first mount. If the id in the URL belongs to a
  // draft that's since been deleted (or was created in another browser),
  // say so and strip the now-stale param — with `draftId` cleared, the
  // normal relay prefill below takes over rather than leaving the user
  // staring at a blank editor.
  useEffect(() => {
    if (!draftId || loadedFrom === draftId) return;
    if (draft) {
      setTitle(draft.title);
      setSummary(draft.summary);
      setContent(draft.content);
      setEditFormat(draft.content ? detectContentFormat(draft.content) : undefined);
      setSavedSnapshot({ title: draft.title, summary: draft.summary, content: draft.content });
      setLoadedFrom(draft.id);
    } else {
      toast({
        title: 'That draft no longer exists',
        description: 'It may have been deleted here or created in another browser. Starting fresh instead.',
      });
      const next = new URLSearchParams(searchParams);
      next.delete('draft');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, draft, loadedFrom]);

  // Prefill from the user's own existing version, or fork from another
  // author's version if one exists (never silently overwrite someone else's
  // content without the user seeing it first). Skipped entirely while a
  // draft is being resumed — the draft IS the source of truth in that
  // case. A stale `draftId` is cleared by the resume effect above, which
  // is what lets this run again in that edge case.
  useEffect(() => {
    if (draftId || loadedFrom !== null) return;

    // Every branch seeds the baseline (the "no unsaved work" reference
    // point) with whatever it prefilled, so content the user didn't
    // type — an existing version being edited, or another author's text
    // being forked — doesn't immediately read as unsaved changes.
    const prefill = (next: EditorSnapshot) => {
      setTitle(next.title);
      setSummary(next.summary);
      setContent(next.content);
      setEditFormat(next.content ? detectContentFormat(next.content) : 'djot');
      setSavedSnapshot((prev) => prev ?? next);
      setLoadedFrom('');
    };

    if (ownVersion) {
      prefill({
        title: ownVersion.tags.find(([n]) => n === 'title')?.[1] ?? prettifySlug(slug),
        summary: ownVersion.tags.find(([n]) => n === 'summary')?.[1] ?? '',
        content: ownVersion.content,
      });
    } else if (existingOtherVersion) {
      prefill({
        title: existingOtherVersion.tags.find(([n]) => n === 'title')?.[1] ?? prettifySlug(slug),
        summary: existingOtherVersion.tags.find(([n]) => n === 'summary')?.[1] ?? '',
        content: existingOtherVersion.content,
      });
    } else if (versions !== undefined) {
      // Versions have loaded and there's genuinely nothing to fork from.
      prefill({ title: prettifySlug(slug), summary: '', content: '' });
    }
  }, [ownVersion, existingOtherVersion, versions, slug, loadedFrom, draftId]);

  const isForking = Boolean(existingOtherVersion && !ownVersion);
  const finalSlug = normalizeWikiTitle(title) || slug;

  // "Dirty" = the editor differs from the last durable reference point.
  // That reference is the saved draft when one exists; otherwise it's
  // whatever the editor was prefilled with (an existing version, a fork
  // source, or an empty new article), so prefilled content never reads
  // as unsaved work the user didn't type.
  const current: EditorSnapshot = { title, summary, content };
  const baseline: EditorSnapshot = savedSnapshot ?? { title: '', summary: '', content: '' };
  const dirty =
    current.title !== baseline.title ||
    current.summary !== baseline.summary ||
    current.content !== baseline.content;

  // Document statistics, computed on the live content string. Cheap
  // enough (O(n) string work) to recompute on every keystroke without
  // debouncing — memoized only to keep the numbers referentially stable.
  const stats = computeEditorStats(content);

  // Drives the header badge. `idle` hides it (nothing worth reporting);
  // `dirty` invites a click to save a *draft*; `saving` while the write
  // to localStorage is in flight; `saved` once there's nothing pending.
  // The badge never claims to publish — a draft is local-only by design.
  const saveState: SaveState = isSavingDraft ? 'saving' : dirty ? 'dirty' : content.trim() ? 'saved' : 'idle';

  // Warn before navigating away with unsaved work — the whole point of
  // drafts is that closing a tab shouldn't quietly destroy writing.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleSaveDraft = () => {
    if (!title.trim() && !summary.trim() && !content.trim()) {
      toast({ title: "There's nothing to save yet.", variant: 'destructive' });
      return;
    }

    setIsSavingDraft(true);
    try {
      const id = drafts.save(
        { title, summary, content, slug: finalSlug },
        // Update the draft this editor session is already tied to, so
        // repeated saves don't stack up entries. `loadedFrom` (rather
        // than `draftId`) is the authority here: it also covers the
        // case where the first save happened this session, before the
        // URL carried any draft id at all.
        loadedFrom || undefined,
      );
      setSavedSnapshot({ title, summary, content });

      // Mark this draft as the editor's current source *before* putting
      // it in the URL. Otherwise the resume effect would see a new
      // `draftId` it hasn't loaded and re-initialize the fields from
      // storage — harmless today (same text), but it would discard any
      // keystroke landing between the save and the re-render.
      setLoadedFrom(id);

      // Anchor the URL to the draft id so a subsequent Save updates the
      // same entry instead of stacking up a new draft per click. `replace`
      // so this doesn't add a history entry the Back button would trip over.
      if (draftId !== id) {
        const next = new URLSearchParams(searchParams);
        next.set('draft', id);
        setSearchParams(next, { replace: true });
      }

      toast({ title: 'Draft saved', description: 'Find it any time under Write → Drafts.' });

      // Best-effort encrypted sync to Nostr, so the draft follows this
      // account to the user's other devices. Fire-and-forget: the local
      // save is the ground truth; sync failing must not pretend it's the
      // draft's fault.
      const saved = drafts.get(id);
      if (saved && draftSync.canSync) {
        void draftSync.syncUp(saved).catch((error) => {
          console.warn('Draft sync failed:', error);
        });
      }
    } catch {
      toast({
        title: "Couldn't save the draft",
        description: 'This browser may be out of storage space.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  /**
   * Inserts markup from the media picker at the end of the content.
   * Inserting at the end is the predictable behavior once focus has moved
   * into the dialog; trying to keep the original textarea selection would
   * risk writing into a stale or wrong position.
   */
  const handleInsertMedia = (markup: string) => {
    const result = insertBlockAtCursor({ value: content, start: content.length, end: content.length }, () => markup);
    setContent(result.value);
  };

  const handlePublish = async () => {
    if (!content.trim()) {
      toast({ title: 'Article content cannot be empty.', variant: 'destructive' });
      return;
    }
    if (!finalSlug) {
      toast({ title: 'Please enter a valid title.', variant: 'destructive' });
      return;
    }

    const tags: string[][] = [['d', finalSlug]];
    if (title.trim()) tags.push(['title', title.trim()]);
    if (summary.trim()) tags.push(['summary', summary.trim()]);
    if (isForking && existingOtherVersion) {
      tags.push(['e', existingOtherVersion.id, '', 'fork']);
      tags.push(['a', `${WIKI_ARTICLE_KIND}:${existingOtherVersion.pubkey}:${finalSlug}`, '', 'fork']);
    }
    tags.push(['alt', `Wiki article: ${title.trim() || finalSlug}`]);

    try {
      // Any media inserted locally (still a `wikistar-media:` placeholder)
      // gets uploaded and rewritten to a real Blossom URL first — a signed
      // event must never ship a link readers can't resolve. A broken image
      // would be permanently broken for every reader, unrecoverable once
      // signed.
      const pending = findPendingMediaHashes(content);
      let resolvedContent = content;
      if (pending.length > 0) {
        const urlByHash = await resolveMedia.mutateAsync(pending);
        resolvedContent = resolvePendingMedia(content, urlByHash);
      }

      // Expand [[wikilink]] shorthand into spec-compliant NIP-54 Djot
      // reference links before publishing, so the signed content is
      // portable to any Nostr wiki client — not just Wikistar.
      const event = await publishEvent({ kind: WIKI_ARTICLE_KIND, content: expandWikiLinks(resolvedContent), tags });

      // A draft that's been published is no longer a draft — keeping it
      // would leave a stale, now-obsolete copy in the drafts list.
      // Uses `loadedFrom` for the same reason as `handleSaveDraft`: it
      // covers a draft first saved during this session, whose id may not
      // be in the URL yet.
      if (loadedFrom) {
        drafts.remove(loadedFrom);
        // Also clean up the synced copy from relays — otherwise the
        // published article's draft would linger in the encrypted store.
        if (draftSync.canSync) {
          void draftSync.deleteSynced(loadedFrom).catch(() => undefined);
        }
      }

      toast({ title: 'Article published!' });
      navigate(`/w/${encodeURIComponent(finalSlug)}/${nip19.npubEncode(event.pubkey)}`);
    } catch (error) {
      toast({
        title: 'Failed to publish article.',
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <Card className="border-dashed">
            <CardContent className="py-12 px-8">
              <p className="mx-auto max-w-sm text-muted-foreground">
                You need to log in with a Nostr key, browser extension, or bunker to write articles.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              <PenSquare className="size-6 shrink-0" />
              <span className="truncate">
                {isForking ? 'Fork article' : ownVersion ? 'Edit article' : 'New article'}
              </span>
            </h1>
            {dirty && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                <FileEdit className="size-3.5" />
                Unsaved
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Focus mode is honestly the only "layout" the editor should
                own — the full view toggles already move to the document/
                format row, and focus mode is even more page-level: it's
                about hiding everything else on screen, not about the
                editor's content. */}
            <Button type="button" variant="outline" size="icon" onClick={() => setFocusMode(true)} aria-label="Enter focus mode">
              <Maximize2 className="size-4" />
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={isPending}>
              <Save className="size-4" />
              Save draft
            </Button>
            <Button onClick={handlePublish} disabled={isPending}>
              {isPending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
              Publish
            </Button>
          </div>
        </div>

        {isForking && (
          <Card className="mb-6 border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
            <CardContent className="py-3 px-4 text-sm text-amber-900 dark:text-amber-300">
              An article with this title already exists by another author. Publishing will create your
              own version (a fork) — the original stays intact and both will be visible as versions of
              the article.
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="article-title">Title</Label>
              <Input
                id="article-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title"
              />
              {finalSlug && <p className="text-xs text-muted-foreground">Slug: {finalSlug}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="article-summary">Summary (optional)</Label>
              <Input
                id="article-summary"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="One-line summary for search results"
              />
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
              <Label>Content</Label>
              <div className="flex flex-wrap items-center gap-2">
                {/* The raw/live/split view is a layout/document choice next
                    to the format selector, not a formatting action — so it
                    sits at the page level rather than inside the editor
                    toolbar (see the editor's avoidance of rendering it
                    internally). */}
                <EditorViewToggle value={viewMode} onChange={setViewMode} />
                <EditorFormatSelect value={editFormatState} onChange={setEditFormat} className="h-8" />
              </div>
            </div>
            {editFormatState === 'asciidoc' ? (
              <AsciiDocEditor
                value={content}
                onChange={setContent}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(editFormatState, 'article')}
              />
            ) : (
              <DjotEditor
                value={content}
                onChange={setContent}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(editFormatState, 'article')}
              />
            )}
          </div>
        </div>

        <MediaPickerDialog
          open={mediaOpen}
          onOpenChange={setMediaOpen}
          format={editFormatState === 'asciidoc' ? 'asciidoc' : 'djot'}
          onInsert={handleInsertMedia}
        />

        {focusMode && (
          <FocusModeView
            title={title.trim() || prettifySlug(slug)}
            statusBadge={
              <SaveStatusBadge
                state={saveState}
                onSave={handleSaveDraft}
              />
            }
            headerControls={
              <>
                <EditorViewToggle value={viewMode} onChange={setViewMode} />
                <EditorFormatSelect value={editFormatState} onChange={setEditFormat} className="h-8" />
              </>
            }
            footer={
              <>
                <span>{stats.lines} lines</span>
                <span>{stats.words} words</span>
                <span title="Character count of the raw source, markup included">
                  {stats.charsWithMarkup} chars
                </span>
                <span title="Character count with markup delimiters and URLs stripped — roughly the readable text">
                  {stats.charsWithoutMarkup} chars without markup
                </span>
              </>
            }
            onClose={() => setFocusMode(false)}
          >
            {editFormatState === 'asciidoc' ? (
              <AsciiDocEditor
                value={content}
                onChange={setContent}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(editFormatState, 'article-focus')}
                fullHeight
              />
            ) : (
              <DjotEditor
                value={content}
                onChange={setContent}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onRequestMedia={() => setMediaOpen(true)}
                onToggleFocus={() => setFocusMode((f) => !f)}
                onSave={handleSaveDraft}
                placeholder={editorPlaceholder(editFormatState, 'article-focus')}
                fullHeight
              />
            )}
          </FocusModeView>
        )}

        {/* Document stats strip — same function as the focus-mode
            footer, kept visible in the normal editor so character-count
            feedback isn't fullscreen-only. */}
        <footer className="mt-3 flex justify-end gap-4 border-t border-border pt-2 text-xs text-muted-foreground">
          <span>{stats.lines} lines</span>
          <span>{stats.words} words</span>
          <span title="Raw source, markup included">{stats.charsWithMarkup} chars</span>
          <span title="Markup and URLs stripped, roughly readable prose">{stats.charsWithoutMarkup} chars without markup</span>
        </footer>
      </div>
    </Layout>
  );
}
